import { useEffect, useRef } from 'react';
import { 
  Building2, 
  Factory, 
  Landmark, 
  Briefcase, 
  Construction 
} from 'lucide-react';

const marketSegments = [
  {
    id: 1,
    icon: Building2,
    title: 'Desarrolladores Inmobiliarios',
    description: 'Socios estables y confiables para el desarrollo de proyectos residenciales, comerciales o mixtos, integrando desde la división de terrenos hasta la urbanización.',
  },
  {
    id: 2,
    icon: Factory,
    title: 'Empresas Industriales',
    description: 'Construcción y expansión de naves y plantas industriales con experiencia en infraestructura pesada y especificaciones técnicas operativas.',
  },
  {
    id: 3,
    icon: Landmark,
    title: 'Instituciones Públicas y Privadas',
    description: 'Edificación y rehabilitación de inmuebles institucionales priorizando calidad, cumplimiento normativo y durabilidad.',
  },
  {
    id: 4,
    icon: Briefcase,
    title: 'Inversionistas y Propietarios',
    description: 'Construcción y remodelación de locales comerciales y oficinas con profesionalismo, acabados de calidad y gestión transparente.',
  },
  {
    id: 5,
    icon: Construction,
    title: 'Proyectos de Infraestructura',
    description: 'Expertos en ingeniería civil y obra pesada para proyectos de gran escala como vialidades y urbanizaciones complejas.',
  },
];

export const MarketSection = () => {
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('active');
          }
        });
      },
      { threshold: 0.1 }
    );

    const elements = sectionRef.current?.querySelectorAll('.reveal');
    elements?.forEach((el) => observer.observe(el));

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="mercado"
      ref={sectionRef}
      data-testid="market-section"
      className="py-20 md:py-32 bg-[#385723] relative overflow-hidden"
    >
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,#ffffff_1px,transparent_1px),linear-gradient(to_bottom,#ffffff_1px,transparent_1px)] bg-[size:48px_48px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-16 reveal">
          <div className="flex items-center justify-center gap-3 mb-4">
            <div className="w-12 h-1 bg-[#BF9000]" />
            <span className="font-manrope uppercase tracking-widest text-xs font-bold text-[#BF9000]">
              A quiénes servimos
            </span>
            <div className="w-12 h-1 bg-[#BF9000]" />
          </div>
          <h2
            data-testid="market-title"
            className="font-oswald text-3xl sm:text-4xl lg:text-5xl font-bold text-white uppercase tracking-tight mb-4"
          >
            Nuestro <span className="text-[#92D050]">Mercado</span>
          </h2>
          <p className="font-manrope text-slate-300 max-w-2xl mx-auto text-base sm:text-lg leading-relaxed">
            Atendemos las necesidades de diversos sectores con soluciones especializadas
          </p>
        </div>

        {/* Market Segments Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {marketSegments.map((segment, index) => {
            const IconComponent = segment.icon;
            return (
              <div
                key={segment.id}
                data-testid={`market-card-${segment.id}`}
                className={`market-card bg-white/5 backdrop-blur-sm p-8 reveal group hover:bg-white transition-all duration-300 ${
                  index === 4 ? 'lg:col-span-1 lg:col-start-2' : ''
                }`}
                style={{ transitionDelay: `${index * 100}ms` }}
              >
                <div className="w-14 h-14 bg-[#92D050] text-[#385723] flex items-center justify-center mb-6 group-hover:bg-[#385723] group-hover:text-white transition-colors duration-300">
                  <IconComponent className="w-7 h-7" />
                </div>
                <h3 className="font-oswald text-xl font-bold text-white group-hover:text-[#385723] uppercase tracking-tight mb-3 transition-colors duration-300">
                  {segment.title}
                </h3>
                <p className="font-manrope text-slate-300 group-hover:text-slate-600 text-sm leading-relaxed transition-colors duration-300">
                  {segment.description}
                </p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
